import { useEffect, useMemo, useState } from "react";
import { api, absUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Sparkles,
  RefreshCcw,
  Loader2,
  Star,
  Trash2,
  Users,
  Crown,
} from "lucide-react";

const ROLE_LABEL = {
  main: "Main",
  father: "Father",
  mother: "Mother",
  sister: "Big Sister",
  baby_brother: "Baby Brother",
  cousin_calvin: "Cousin",
  cousin_louis: "Cousin",
  cousin_stephan: "Cousin",
  grandfather: "Grandpa",
  grandmother: "Grandma",
};

const ROLE_ACCENT = {
  main: "from-rose-200 to-amber-200 text-rose-800",
  father: "from-sky-200 to-indigo-200 text-indigo-800",
  mother: "from-pink-200 to-rose-200 text-rose-800",
  sister: "from-fuchsia-200 to-purple-200 text-purple-800",
  baby_brother: "from-yellow-200 to-amber-200 text-amber-800",
  cousin_calvin: "from-emerald-200 to-teal-200 text-emerald-800",
  cousin_louis: "from-orange-200 to-amber-200 text-amber-800",
  cousin_stephan: "from-cyan-200 to-sky-200 text-sky-800",
  grandfather: "from-stone-300 to-amber-300 text-stone-800",
  grandmother: "from-violet-200 to-pink-200 text-violet-800",
};

const VIEW_LABEL = {
  portrait: "Portrait",
  profile: "Side",
  full_body: "Full",
  expressions: "Expressions",
};

function ViewThumbs({ views }) {
  if (!views?.length) return null;
  return (
    <div className="grid grid-cols-4 gap-2">
      {views.map((v) => (
        <div
          key={v.view}
          data-testid={`view-thumb-${v.view}`}
          className="relative aspect-square overflow-hidden rounded-lg bg-stone-100"
          title={`${VIEW_LABEL[v.view] || v.view} — ${v.status}`}
        >
          {v.image_url ? (
            <img
              src={absUrl(v.image_url)}
              alt={v.view}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-stone-400">
              {v.status === "running" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : v.status === "failed" ? (
                <span className="text-red-500">×</span>
              ) : (
                "—"
              )}
            </div>
          )}
          <span className="absolute bottom-0 left-0 right-0 bg-black/40 px-1 py-0.5 text-[9px] text-white">
            {VIEW_LABEL[v.view] || v.view}
          </span>
        </div>
      ))}
    </div>
  );
}

function FamilyMemberCard({ m, onSetMain, onRegenerate, onDelete }) {
  const portrait = m.views?.find((v) => v.view === "portrait");
  const accent = ROLE_ACCENT[m.role] || "from-stone-200 to-stone-300 text-stone-800";
  const ageStr = m.age != null ? ` · ${m.age}y` : "";

  return (
    <Card
      data-testid={`family-member-${m.role}`}
      className={`overflow-hidden border bg-white/90 shadow-sm transition hover:shadow-md ${
        m.is_active_main
          ? "border-amber-400 ring-2 ring-amber-200"
          : "border-stone-200"
      }`}
    >
      <CardHeader className="flex-row items-center gap-4 space-y-0 pb-3">
        <div
          className={`h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center text-2xl font-bold ring-1 ring-white/40`}
        >
          {portrait?.image_url ? (
            <img
              src={absUrl(portrait.image_url)}
              alt={m.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span>{m.name?.[0] || "?"}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <CardTitle className="truncate text-lg">{m.name}</CardTitle>
            {m.is_active_main ? (
              <Badge className="bg-amber-500 hover:bg-amber-500">
                <Crown className="mr-1 h-3 w-3" />
                Main
              </Badge>
            ) : null}
          </div>
          <CardDescription className="truncate">
            <span className="font-medium">{ROLE_LABEL[m.role] || m.role_label}</span>
            {ageStr}
            {m.full_name && m.full_name !== m.name ? (
              <span className="text-stone-400"> · {m.full_name}</span>
            ) : null}
          </CardDescription>
          <div className="mt-1 flex items-center gap-2">
            <Badge
              data-testid={`status-${m.role}`}
              className={
                m.status === "ready"
                  ? "bg-emerald-600 hover:bg-emerald-600"
                  : m.status === "failed"
                  ? "bg-red-600 hover:bg-red-600"
                  : "bg-amber-500 hover:bg-amber-500"
              }
            >
              {m.status}
            </Badge>
            <Badge variant="outline" className="border-stone-300 text-[10px]">
              {m.style}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <ViewThumbs views={m.views} />
        <div className="flex flex-wrap gap-2 pt-1">
          {!m.is_active_main && m.role === "main" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSetMain(m.id)}
              data-testid={`set-main-${m.role}`}
              className="border-amber-300 text-amber-700"
            >
              <Star className="mr-1 h-3 w-3" />
              Set as main
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            data-testid={`regen-${m.role}`}
            onClick={() => onRegenerate(m.id)}
            disabled={m.status === "generating"}
            className="border-stone-300"
          >
            {m.status === "generating" ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <RefreshCcw className="mr-1 h-3 w-3" />
            )}
            Regenerate
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid={`delete-${m.role}`}
            onClick={() => onDelete(m.id)}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Family() {
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [error, setError] = useState(null);

  const refresh = async () => {
    try {
      const [m, t] = await Promise.all([
        api.get("/family/members"),
        api.get("/family/templates"),
      ]);
      setMembers(m.data);
      setTemplates(t.data.templates);
      setLoaded(true);
    } catch (exc) {
      setError(exc?.response?.data?.detail || exc.message || "Load failed");
      setLoaded(true);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Poll while anything is generating
  const anyGenerating = useMemo(
    () => members.some((m) => m.status === "generating"),
    [members],
  );

  useEffect(() => {
    if (!anyGenerating) return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [anyGenerating]);

  const generateAll = async () => {
    setError(null);
    setBulkRunning(true);
    try {
      const r = await api.post("/family/generate-all", {
        style: "pixar_3d",
        regenerate_main: false,
      });
      // Optimistic refresh
      await refresh();
      console.info("[family] generate-all:", r.data);
    } catch (exc) {
      setError(exc?.response?.data?.detail || exc.message || "Bulk generate failed");
    } finally {
      setBulkRunning(false);
    }
  };

  const onRegenerate = async (id) => {
    try {
      await api.post(`/family/members/${id}/regenerate`);
      refresh();
    } catch (exc) {
      setError(exc?.response?.data?.detail || exc.message || "Regenerate failed");
    }
  };

  const onSetMain = async (id) => {
    await api.patch(`/family/members/${id}`, { is_active_main: true });
    refresh();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this family member? This cannot be undone."))
      return;
    await api.delete(`/family/members/${id}`);
    refresh();
  };

  if (!loaded) {
    return (
      <div className="flex justify-center py-20 text-stone-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  // Determine which template roles haven't been created yet
  const existingRoles = new Set(members.map((m) => m.role));
  const missingRoles = templates.filter((t) => !existingRoles.has(t.role));

  return (
    <div data-testid="page-family" className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold text-stone-800">
            <Users className="h-7 w-7 text-rose-500" />
            Family Cast
            {members.length > 0 && members.every((m) => m.is_approved) ? (
              <Badge
                data-testid="family-approved-badge"
                className="ml-2 bg-emerald-600 text-white hover:bg-emerald-600"
              >
                Approved
              </Badge>
            ) : null}
          </h1>
          <p className="text-sm text-stone-600">
            The Doan family — Cocomelon-style multi-character world for{" "}
            <strong>Dom&apos;s Journey</strong>. {members.length} member
            {members.length === 1 ? "" : "s"} · {missingRoles.length} missing.
          </p>
        </div>
        <Button
          data-testid="generate-all-btn"
          onClick={generateAll}
          disabled={bulkRunning || (missingRoles.length === 0 && !anyGenerating)}
          className="rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-md"
        >
          {bulkRunning ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="mr-2 h-4 w-4" />
          )}
          {missingRoles.length > 0
            ? `Generate ${missingRoles.length} missing member${missingRoles.length === 1 ? "" : "s"}`
            : "All members present"}
        </Button>
      </div>

      {error ? (
        <div
          data-testid="family-error"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"
        >
          {error}
        </div>
      ) : null}

      {anyGenerating ? (
        <div
          data-testid="family-generating-banner"
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Generating character designs via nano-banana — auto-refreshing every 4s.
        </div>
      ) : null}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {members.map((m) => (
          <FamilyMemberCard
            key={m.id}
            m={m}
            onSetMain={onSetMain}
            onRegenerate={onRegenerate}
            onDelete={onDelete}
          />
        ))}

        {missingRoles.map((t) => (
          <Card
            key={t.role}
            data-testid={`family-missing-${t.role}`}
            className="border-2 border-dashed border-stone-300 bg-white/60 shadow-none"
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${
                    ROLE_ACCENT[t.role] || "from-stone-200 to-stone-300 text-stone-700"
                  } text-xl font-bold opacity-70`}
                >
                  {t.name[0]}
                </div>
                <div>
                  <CardTitle className="text-lg text-stone-700">
                    {t.name}
                  </CardTitle>
                  <CardDescription>
                    {t.label}
                    {t.age != null ? ` · ${t.age}y` : ""} · {t.full_name}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs italic text-stone-500">
                Not generated yet — click <strong>Generate missing members</strong> above.
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
